import { EnvironmentOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Modal, Pagination, Popconfirm } from 'antd'
import { ModalProps } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import {
  useTreatmentServiceAcceptRequestToAccessEpidemiologyCaseEndpoint,
  useTreatmentServiceGetAccessRequestsListEndpoint,
  useTreatmentServiceGetAccessRequestsListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  EpidemiologyCaseAccessRequestStatus,
  Permissions
} from 'src/api/requests'
import { message, Paragraph, Text } from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearHourMinutesFormatSeparator } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { RefuseAccessRequestModal } from './RefuseAccessRequestModal'
import { useStyles } from './styles'

type Props = {
  onCancel: () => void
} & ModalProps

export const EpidemiologyRequestsModal = ({ onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const palette = useTheme()

  const { userData, hasPermission } = useUser()
  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const [currentId, setCurrentId] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    institutionId: userData?.currentLegalEntityId,
    status: EpidemiologyCaseAccessRequestStatus.NEW
  }

  const { data, isLoading } =
    useTreatmentServiceGetAccessRequestsListEndpoint(queryParams)

  const { mutate: accept } =
    useTreatmentServiceAcceptRequestToAccessEpidemiologyCaseEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(t('epidemiology.success_accept_request'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetAccessRequestsListEndpointKey]
        })
      }
    })

  const onAcceptRequest = (id?: string) => accept({ id: String(id) })

  const onCloseModal = () => setCurrentId(undefined)

  useEffect(() => {
    if (data && data.totalCount === 0) {
      onCancel()
    }
  }, [data])

  return (
    <Modal
      loading={isLoading}
      centered
      open
      footer={null}
      title={t('epidemiology.access_requests_title')}
      onCancel={onCancel}
      maskClosable={false}
      width={920}
      {...props}
    >
      <Flex vertical gap={8}>
        {data?.data?.map((item) => (
          <Flex
            key={item.id}
            justify="space-between"
            align="center"
            className={styles.card}
            gap={16}
          >
            <Flex vertical gap={8}>
              <Flex
                align="center"
                gap={8}
                style={{ color: palette.grey.grey1 }}
              >
                <Text color={palette.grey.grey1}>
                  {dayjs
                    .utc(item.createdAt)
                    .tz()
                    .format(dayMonthYearHourMinutesFormatSeparator)}{' '}
                </Text>
                <Divider className={styles.verticalDivider} type="vertical" />
                <EnvironmentOutlined />
                <Text>{item.institution?.name}</Text>
              </Flex>

              <Text weight={400}>
                {t('epidemiology.access_request_description', {
                  simeCode: item.patient?.simeCode,
                  lastName: item.patient?.lastName,
                  firstName: item.patient?.firstName
                })}
              </Text>
              <Paragraph ellipsis={{ rows: 2, tooltip: item.reason }}>
                {t('cases.request_reason')}: {item.reason}
              </Paragraph>
            </Flex>

            {hasPermission(
              Permissions.TREATMENT_EPIDEMIOLOGY_CASE_MANAGE_REQUEST_ALL
            ) && (
              <Flex gap={8}>
                <Button
                  danger
                  type="primary"
                  onClick={() => setCurrentId(item.id)}
                >
                  {t('stock_administration.reject')}
                </Button>

                <Popconfirm
                  title={t('epidemiology.confirm_access_request')}
                  onConfirm={() => onAcceptRequest(item.id)}
                >
                  <Button type="primary">
                    {t('stock_administration.accept')}
                  </Button>
                </Popconfirm>
              </Flex>
            )}
          </Flex>
        ))}

        <Pagination {...pagination} total={data?.totalCount} />
      </Flex>

      {currentId && (
        <RefuseAccessRequestModal onCancel={onCloseModal} id={currentId} />
      )}
    </Modal>
  )
}
